import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCurrentWhitelist,
  uploadOrUpdateWhitelist,
  deleteCurrentWhitelist,
  getWhitelistVersionsById,
} from './singleWhitelistApi';
import { toast } from '@/utils/toast';
import { useDownloadFile } from '@/hooks/useDownloadFile';

// Query keys
const QUERY_KEYS = {
  currentWhitelist: ['current-whitelist'] as const,
  whitelistVersionsBase: ['whitelist-versions'] as const,
  whitelistVersions: (whitelistId?: number) =>
    ['whitelist-versions', whitelistId ?? 'none'] as const,
};

// Hook to get current whitelist
export function useCurrentWhitelist() {
  return useQuery({
    queryKey: QUERY_KEYS.currentWhitelist,
    queryFn: getCurrentWhitelist,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook to get whitelist versions
export function useWhitelistVersions(whitelistId?: number, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.whitelistVersions(whitelistId),
    queryFn: () => {
      if (!whitelistId) {
        return Promise.resolve({ data: [] });
      }
      return getWhitelistVersionsById(whitelistId);
    },
    enabled: enabled && !!whitelistId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook to upload or update whitelist
export function useUploadOrUpdateWhitelist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      existingWhitelistId,
    }: {
      file: File;
      existingWhitelistId?: number;
    }) => uploadOrUpdateWhitelist(file, existingWhitelistId),
    onSuccess: (_, { existingWhitelistId }) => {
      // Invalidate and refetch both current whitelist and versions
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentWhitelist });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.whitelistVersionsBase,
      });

      const action = existingWhitelistId ? 'updated' : 'created';
      toast.success(`Whitelist ${action} successfully`);
    },
    onError: (error: Error, { existingWhitelistId }) => {
      const action = existingWhitelistId ? 'update' : 'create';
      toast.error(`Failed to ${action} whitelist`);
    },
  });
}

// Hook to delete current whitelist
export function useDeleteCurrentWhitelist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCurrentWhitelist,
    onSuccess: () => {
      // Invalidate and refetch both current whitelist and versions
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentWhitelist });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.whitelistVersionsBase,
      });
      toast.success('Whitelist deleted successfully');
    },
    onError: (_error: Error) => {
      toast.error('Failed to delete whitelist');
    },
  });
}

// Hook to download whitelist file
export function useDownloadWhitelistFile() {
  const { download, downloadFromApi, isLoading, progress, error: _error } =
    useDownloadFile({
      successMessage: 'Whitelist file downloaded successfully',
      errorMessage: 'Failed to download whitelist file',
    });

  const downloadWhitelistFile = async ({
    fileUrl,
    apiEndpoint,
    filename,
  }: {
    fileUrl?: string;
    apiEndpoint?: string;
    filename: string;
  }) => {
    if (apiEndpoint) {
      return await downloadFromApi(apiEndpoint, filename);
    } else if (fileUrl) {
      return await download(fileUrl, filename);
    } else {
      throw new Error('Either fileUrl or apiEndpoint must be provided');
    }
  };

  return {
    mutate: downloadWhitelistFile,
    mutateAsync: downloadWhitelistFile,
    isLoading,
    progress,
    error: _error,
  };
}
